import { CWY_NETWORK } from '../config/cwyConfig';
import type { CwyTransferRequest, CwyTransferRequestType } from '../services/walletService';
import { formatTransactionTimestamp } from '../../../utils/formatTransactionTimestamp';

export type TransactionTimeframe = 'all' | '7d' | '30d' | '90d' | 'year';
export type TransactionSortOrder = 'desc' | 'asc';
export type TransactionTypeFilter = 'all' | 'cwy_grant' | 'vault_deposit' | 'on_chain' | 'off_chain';
export type TransactionStatusFilter = 'all' | CwyTransferRequest['status'];
export type TransactionPageSize = 5 | 10 | 25 | 'all';

export interface TransactionLogFilters {
  timeframe: TransactionTimeframe;
  sortOrder: TransactionSortOrder;
  typeFilter: TransactionTypeFilter;
  statusFilter: TransactionStatusFilter;
  pageSize: TransactionPageSize;
  page: number;
}

export const DEFAULT_TRANSACTION_LOG_FILTERS: TransactionLogFilters = {
  timeframe: 'all',
  sortOrder: 'desc',
  typeFilter: 'all',
  statusFilter: 'all',
  pageSize: 5,
  page: 1,
};

function requestTimestamp(request: CwyTransferRequest): number {
  const value = request.tx_confirmed_at ?? request.reviewed_at ?? request.createdAt;
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

function timeframeStart(timeframe: TransactionTimeframe): number | null {
  if (timeframe === 'all') return null;
  const now = new Date();
  if (timeframe === 'year') {
    return new Date(now.getFullYear(), 0, 1).getTime();
  }
  const days = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 90;
  return now.getTime() - days * 24 * 60 * 60 * 1000;
}

export function requestTypeLabel(requestType?: CwyTransferRequestType | string): string {
  if (requestType === 'treasury_deposit') return 'Vault deposit';
  return 'CWY grant';
}

function matchesTypeFilter(request: CwyTransferRequest, filter: TransactionTypeFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'cwy_grant') {
    return (request.request_type ?? 'phase1_test_grant') === 'phase1_test_grant';
  }
  if (filter === 'vault_deposit') return request.request_type === 'treasury_deposit';
  if (filter === 'on_chain') return Boolean(request.tx_signature?.trim());
  if (filter === 'off_chain') return !request.tx_signature?.trim();
  return true;
}

export function filterAndSortTransactions(
  requests: CwyTransferRequest[],
  filters: TransactionLogFilters
): CwyTransferRequest[] {
  const start = timeframeStart(filters.timeframe);

  let result = requests.filter((request) => {
    if (start !== null && requestTimestamp(request) < start) return false;
    if (!matchesTypeFilter(request, filters.typeFilter)) return false;
    if (filters.statusFilter !== 'all' && request.status !== filters.statusFilter) return false;
    return true;
  });

  result = [...result].sort((a, b) => {
    const diff = requestTimestamp(a) - requestTimestamp(b);
    return filters.sortOrder === 'asc' ? diff : -diff;
  });

  return result;
}

export function paginateTransactions(
  items: CwyTransferRequest[],
  pageSize: TransactionPageSize,
  page: number
): { pageItems: CwyTransferRequest[]; totalPages: number; total: number; safePage: number } {
  const total = items.length;
  if (pageSize === 'all') {
    return { pageItems: items, totalPages: 1, total, safePage: 1 };
  }
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    pageItems: items.slice(start, start + pageSize),
    totalPages,
    total,
    safePage,
  };
}

function csvEscape(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

export function exportTransactionsCsv(requests: CwyTransferRequest[], timeZone?: string): void {
  const headers = [
    'Request ID',
    'Type',
    'Status',
    'Amount CWY',
    'Wallet',
    'Network',
    'Requested At (UTC)',
    'Requested At (Local)',
    'Reviewed At (Local)',
    'Confirmed At (Local)',
    'Transaction Signature',
    'Rejection Reason',
    'Error',
  ];

  const rows = requests.map((r) =>
    [
      r.transfer_request_id,
      requestTypeLabel(r.request_type),
      r.status,
      String(r.amount_cwy),
      r.wallet_address,
      CWY_NETWORK,
      r.createdAt ?? '',
      formatTransactionTimestamp(r.createdAt, timeZone),
      r.reviewed_at ? formatTransactionTimestamp(r.reviewed_at, timeZone) : '',
      r.tx_confirmed_at ? formatTransactionTimestamp(r.tx_confirmed_at, timeZone) : '',
      r.tx_signature ?? '',
      r.rejection_reason ?? '',
      r.tx_error ?? '',
    ]
      .map((cell) => csvEscape(cell))
      .join(',')
  );

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.download = `cwy-transaction-logs-${stamp}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function hasActiveFilters(filters: TransactionLogFilters): boolean {
  return (
    filters.timeframe !== 'all' ||
    filters.typeFilter !== 'all' ||
    filters.statusFilter !== 'all' ||
    filters.sortOrder !== 'desc' ||
    filters.pageSize !== 5
  );
}
